// src/apollo/importClient.js
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  defaultDataIdFromObject,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import fetch from "isomorphic-fetch";

/* ------------------------------------------------------------------ */
/*  Config / constants                                                */
/* ------------------------------------------------------------------ */
const DEFAULT_TOKEN = process.env.GATSBY_CRAFT_API_EXPORT_TOKEN;
const GRAPHQL_URL =
  process.env.GATSBY_CRAFT_API_IMPORTEXPORT_URL || "https://example.com/api";

/* ------------------------------------------------------------------ */
/*  Helper: recursively add __typename + siteId                       */
/* ------------------------------------------------------------------ */
function enrich(obj, siteId) {
  if (Array.isArray(obj)) return obj.map(v => enrich(v, siteId));

  if (obj && typeof obj === "object") {
    const copy = { ...obj };

    // 1) __typename from typeHandle
    if (copy.typeHandle && !copy.__typename) copy.__typename = copy.typeHandle;

    // 2) inherit siteId when missing
    if (copy.id && copy.siteId == null) copy.siteId = siteId;

    // recurse
    Object.keys(copy).forEach(k => {
      copy[k] = enrich(copy[k], siteId);
    });

    return copy;
  }
  return obj;
}

function normalizeSiteId(v) {
  if (Array.isArray(v)) return Number(v[0]) || 1;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

const enrichLink = new ApolloLink((operation, forward) =>
  forward(operation).map(response => {
    const opSiteId = normalizeSiteId(operation.variables?.siteId);
    if (response.data) response.data = enrich(response.data, opSiteId);
    return response;
  })
);

/* ------------------------------------------------------------------ */
/*  HTTP + auth links                                                 */
/* ------------------------------------------------------------------ */
const httpLink = createHttpLink({
  uri: GRAPHQL_URL,
  fetch,
});

const authLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    authorization: `Bearer ${localStorage.getItem(
      "GATSBY_CRAFT_API_EXPORT_TOKEN"
    )}`,
  },
}));

const link = ApolloLink.from([authLink, enrichLink, httpLink]);

/* ------------------------------------------------------------------ */
/*  Cache with site-aware IDs                                         */
/* ------------------------------------------------------------------ */
// GOCHA: This means siteId needs to be returned with all entry queries, else there will be cache conflicts:
const cache = new InMemoryCache({
  dataIdFromObject(obj) {
    if (
      obj &&
      obj.__typename &&
      (obj.id !== undefined || obj.siteHandle !== undefined)
    ) {
      const rawSite = obj.siteId ?? obj.siteHandle ?? 1;
      const site =
        typeof rawSite === "string" ? Number(rawSite) || rawSite : rawSite;
      return `${obj.__typename}:${obj.id}:${site}`;
    }
    return defaultDataIdFromObject(obj);
  },
});

/* ------------------------------------------------------------------ */
/*  Client                                                            */
/* ------------------------------------------------------------------ */
export const importClient = new ApolloClient({
  link,
  cache,
  ssrMode: true,
});

/* default export for eslint “prefer default export” */
export default importClient;
